import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Tag, Typography } from 'antd';
import {
  AimOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  CompassOutlined,
  DatabaseOutlined,
  FieldTimeOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { buildGoalInsight } from './goalInsight';
import { profileTimeText, timeText } from './SimpleWorkspaceSupport';
import styles from './GoalInsightPanel.less';

const { Paragraph, Text } = Typography;

const INDICATOR_ICONS = {
  'training-minutes': <FieldTimeOutlined />,
  'active-days': <CalendarOutlined />,
  capability: <SafetyCertificateOutlined />,
  'training-sessions': <DatabaseOutlined />,
};

function indicatorIcon(key) {
  return key.startsWith('body-') ? <AimOutlined /> : INDICATOR_ICONS[key];
}

function directionIcon(direction) {
  if (direction === 'up') return <ArrowUpOutlined />;
  if (direction === 'down') return <ArrowDownOutlined />;
  return <ArrowRightOutlined />;
}

function insightTimeText(value) {
  const normalized = timeText(value);
  return normalized === '-' ? profileTimeText(value) : normalized;
}

function sourceStatusText(status) {
  if (status === 'available') return '已读取';
  if (status === 'loading') return '读取中';
  return '待积累';
}

export default function GoalInsightPanel({ model, actions }) {
  const insight = useMemo(() => buildGoalInsight(model), [model]);
  const problemCount = insight.problems.length;

  return (
    <section className={styles.goalInsight} aria-label="当前目标与训练洞察">
      <div className={styles.insightHero}>
        <div className={styles.heroGlow} />
        <header className={styles.heroHeader}>
          <div>
            <div className={styles.eyebrow}>
              <CompassOutlined />
              CURRENT TRAINING GOAL
            </div>
            <div className={styles.goalLine}>
              <h2>{insight.goal}</h2>
              <span
                className={`${styles.statusPill} ${
                  styles[insight.decision.tone]
                }`}
              >
                <span className={styles.statusPulse} />
                {insight.decision.label}
              </span>
            </div>
            <div className={styles.goalMeta}>
              <span>
                {insight.goalTimeLabel}：
                {insightTimeText(insight.goalRecordedAt)}
              </span>
              {insight.asOf && <span>判断截至：{timeText(insight.asOf)}</span>}
            </div>
          </div>
          <div className={styles.readinessBlock}>
            <Text className={styles.readinessLabel}>当前证据状态</Text>
            <strong>{insight.readiness.label}</strong>
            <span>
              当前页面已读取 {insight.availableSourceCount} /{' '}
              {insight.sources.length} 类数据
            </span>
          </div>
        </header>

        <div className={styles.insightColumns}>
          <article className={styles.insightColumn}>
            <div className={styles.columnLabel}>
              <span className={styles.columnIndex}>01</span>
              系统当前判断
            </div>
            <Paragraph className={styles.assessmentText} ellipsis={{ rows: 4 }}>
              {insight.assessment}
            </Paragraph>
            <div className={styles.confidenceLine}>
              <CheckCircleFilled />
              {insight.confidence}
              {insight.evidenceCount > 0 &&
                ` · ${insight.evidenceCount} 条直接依据`}
            </div>
          </article>

          <article
            className={`${styles.insightColumn} ${styles.problemColumn}`}
          >
            <div className={styles.columnLabel}>
              <span className={styles.columnIndex}>02</span>
              当前主要问题
              {problemCount > 1 && <Tag>{problemCount} 项</Tag>}
            </div>
            {insight.primaryProblem ? (
              <>
                <h3>{insight.primaryProblem.title || '需要持续关注'}</h3>
                <Paragraph
                  className={styles.problemRationale}
                  ellipsis={{ rows: 3 }}
                >
                  {insight.primaryProblem.rationale ||
                    '该问题已进入当前训练判断。'}
                </Paragraph>
              </>
            ) : (
              <Paragraph className={styles.emptyInsight}>
                暂无已确认的主要问题；继续积累训练、能力和反馈证据。
              </Paragraph>
            )}
          </article>

          <article className={`${styles.insightColumn} ${styles.adviceColumn}`}>
            <div className={styles.columnLabel}>
              <span className={styles.columnIndex}>03</span>
              下一步训练方向
            </div>
            <Paragraph className={styles.adviceText} ellipsis={{ rows: 4 }}>
              {insight.recommendation}
            </Paragraph>
            <div className={styles.reviewLine}>
              {insight.reviewDueAt
                ? `建议复查：${timeText(insight.reviewDueAt)}`
                : '建议会随新增证据持续复评'}
            </div>
          </article>
        </div>

        <footer className={styles.evidenceRail}>
          <span className={styles.evidenceLabel}>判断依据</span>
          {insight.sources.map((source) => (
            <button
              type="button"
              key={source.key}
              className={`${styles.evidenceChip} ${
                source.status === 'available'
                  ? styles.evidenceAvailable
                  : source.status === 'loading'
                  ? styles.evidenceLoading
                  : styles.evidenceMissing
              }`}
              onClick={() => actions.changeActiveTab(source.tab)}
            >
              <span />
              {source.label}
              <small>{sourceStatusText(source.status)}</small>
            </button>
          ))}
          <Text className={styles.evidenceNotice}>
            系统建议，不替代医疗诊断
          </Text>
        </footer>
      </div>

      <div className={styles.metricSectionHeader}>
        <div>
          <span className={styles.metricKicker}>GOAL-RELATED CHANGES</span>
          <h3>与当前目标相关的数据与变化</h3>
        </div>
        <Text type="secondary">不合成无法解释的综合分</Text>
      </div>
      <div className={styles.indicatorGrid}>
        {insight.indicators.map((indicator) => (
          <button
            type="button"
            key={indicator.key}
            className={styles.indicatorCard}
            onClick={() => actions.changeActiveTab(indicator.tab)}
          >
            <span className={styles.indicatorIcon}>
              {indicatorIcon(indicator.key)}
            </span>
            <span className={styles.indicatorBody}>
              <span className={styles.indicatorLabel}>{indicator.label}</span>
              <span className={styles.indicatorValueLine}>
                <strong>{indicator.value}</strong>
                <small>{indicator.unit}</small>
              </span>
              <span
                className={`${styles.indicatorChange} ${
                  styles[`direction-${indicator.direction}`]
                }`}
              >
                {directionIcon(indicator.direction)}
                {indicator.change}
              </span>
              <span className={styles.indicatorSource}>{indicator.source}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

GoalInsightPanel.propTypes = {
  model: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
};
